// === IMPORTS ===
import React, { useState, useEffect } from 'react';
import {
  OfficeLayout,
  FurnitureCategory,
  FurnitureType,
  PlacedFurniture,
  AIAgent,
  IsoTileType,
  EditorTool,
  FloorMaterialOption,
  InteractionTypeDefinition,
  MCPToolDefinition,
  AgentAvatarSpriteSet,
} from '../types';
import {
  FURNITURE_CATALOG,
  AGENT_ROLES_CONFIG,
  DEFAULT_FLOOR_MATERIALS,
  DEFAULT_INTERACTION_TYPES,
  DEFAULT_MCP_TOOLS,
  addCustomFurnitureDefinition,
} from '../data/officeCatalog';
import { InteractionManagerModal } from './InteractionManagerModal';
import { RoomManager } from './RoomManager';
import { ConfirmDeleteModal } from './ConfirmDeleteModal';
import { AgentAvatarSpriteModal } from './AgentAvatarSpriteModal';
import { FloorMaterialDrawer } from './FloorMaterialDrawer';
import { CreateFurnitureDrawer } from './CreateFurnitureDrawer';
import { FloorTexturePreviewBox } from './FloorTexturePreviewBox';
import AppIcon from './AppIcon';
import {
  Plus,
  PlusCircle,
  Sparkles,
  Grid,
  Paintbrush,
  Layers,
  AlertTriangle,
  X,
  ChevronDown,
  ChevronUp,
  Check,
  Zap,
  Box,
  Bot,
  Settings,
  UserMinus,
} from 'lucide-react';
import './OfficeEditor.css';

// Helper to create clean graphic drag image (icon / sprite image without text card pill)
export function createGraphicDragImage(e: React.DragEvent, icon: string, customImage?: string) {
  const ghost = document.createElement('div');
  ghost.style.position = 'fixed';
  ghost.style.top = '-9999px';
  ghost.style.left = '-9999px';
  ghost.style.width = '48px';
  ghost.style.height = '48px';
  ghost.style.borderRadius = '12px';
  ghost.style.backgroundColor = '#0f172a';
  ghost.style.border = '2px solid #38bdf8';
  ghost.style.display = 'flex';
  ghost.style.alignItems = 'center';
  ghost.style.justifyContent = 'center';
  ghost.style.boxShadow = '0 10px 25px -5px rgba(0, 0, 0, 0.5)';
  ghost.style.pointerEvents = 'none';
  ghost.style.zIndex = '99999';

  const svgOrImg = (e.currentTarget as HTMLElement).querySelector('svg, img');
  if (svgOrImg) {
    const clone = svgOrImg.cloneNode(true) as HTMLElement;
    clone.style.width = '28px';
    clone.style.height = '28px';
    clone.style.color = '#38bdf8';
    ghost.appendChild(clone);
  }

  document.body.appendChild(ghost);
  e.dataTransfer.setDragImage(ghost, 24, 24);

  setTimeout(() => {
    if (document.body.contains(ghost)) {
      document.body.removeChild(ghost);
    }
  }, 50);
}

interface OfficeEditorProps {
  layout: OfficeLayout;
  onLayoutChange: (updatedLayout: OfficeLayout) => void;
  activeTool: EditorTool;
  setActiveTool: (tool: EditorTool) => void;
  selectedFurnitureType: FurnitureType | null;
  setSelectedFurnitureType: (type: FurnitureType | null) => void;
  selectedFloorType: IsoTileType | null;
  setSelectedFloorType: (type: IsoTileType | null) => void;
  floorMaterials?: FloorMaterialOption[];
  onFloorMaterialsChange?: React.Dispatch<React.SetStateAction<FloorMaterialOption[]>>;
  currentRoomName: string;
  setCurrentRoomName: (name: string) => void;
  selectedItem: PlacedFurniture | null;
  onSelectItem?: (item: PlacedFurniture | null) => void;
  selectedAgent: AIAgent | null;
  onAgentSelect?: (agent: AIAgent | null) => void;
  selectedRoomId?: string | null;
  onSelectRoom?: (roomId: string | null) => void;
  roomDraftBounds?: {
    col: number;
    row: number;
    width: number;
    height: number;
    color?: string;
    name?: string;
    icon?: string;
  } | null;
  setRoomDraftBounds?: (
    bounds: {
      col: number;
      row: number;
      width: number;
      height: number;
      color?: string;
      name?: string;
      icon?: string;
    } | null
  ) => void;
  onOverlapPrevented?: (targetRoomName: string, conflictingRoomName: string) => void;
  onStartSimulation: () => void;
  interactionTypes?: InteractionTypeDefinition[];
  onSaveInteraction?: (interaction: InteractionTypeDefinition) => void;
  onDeleteInteraction?: (id: string) => void;
  onOpenDeleteFloorModal?: (mat: FloorMaterialOption) => void;
  onOpenDeleteFurnitureModal?: (target: { type: FurnitureType; label?: string; icon?: string; id?: string }) => void;
  activeStep?: 1 | 2 | 3 | 4;
  onStepChange?: (step: 1 | 2 | 3 | 4) => void;
}

// === COMPONENTE: SIDEBAR EDITOR DE OFICINA (4 PASOS: ZONAS, SUELO, RECURSOS, AGENTES) ===
export const OfficeEditor: React.FC<OfficeEditorProps> = ({
  layout,
  onLayoutChange,
  activeTool,
  setActiveTool,
  selectedFurnitureType,
  setSelectedFurnitureType,
  selectedFloorType,
  setSelectedFloorType,
  floorMaterials,
  onFloorMaterialsChange,
  selectedItem,
  onSelectItem,
  selectedAgent,
  onAgentSelect,
  selectedRoomId,
  onSelectRoom,
  roomDraftBounds,
  setRoomDraftBounds,
  onOverlapPrevented,
  interactionTypes,
  onSaveInteraction,
  onDeleteInteraction,
  onOpenDeleteFloorModal,
  activeStep: externalActiveStep,
  onStepChange: externalOnStepChange,
}) => {
  // Step Workflow: 1 = Rooms & Zones, 2 = Flooring & Grid, 3 = Resources, 4 = Agents
  const [internalActiveStep, setInternalActiveStep] = useState<1 | 2 | 3 | 4>(1);
  const activeStep = externalActiveStep !== undefined ? externalActiveStep : internalActiveStep;
  const setActiveStep = (step: 1 | 2 | 3 | 4) => {
    if (externalOnStepChange) {
      externalOnStepChange(step);
    } else {
      setInternalActiveStep(step);
    }
  };

  // Interaction Manager State
  const [showInteractionManagerModal, setShowInteractionManagerModal] = useState<boolean>(false);
  const [interactionManagerTab, setInteractionManagerTab] = useState<'catalog' | 'create' | 'guide'>('catalog');

  // Fallback local interaction types
  const [localInteractionTypes, setLocalInteractionTypes] = useState<InteractionTypeDefinition[]>(DEFAULT_INTERACTION_TYPES);
  const activeInteractionTypes = interactionTypes || localInteractionTypes;

  const handleSaveInteraction = (interaction: InteractionTypeDefinition) => {
    if (onSaveInteraction) {
      onSaveInteraction(interaction);
    } else {
      setLocalInteractionTypes((prev) => {
        const idx = prev.findIndex((i) => i.id === interaction.id);
        if (idx >= 0) {
          const copy = [...prev];
          copy[idx] = interaction;
          return copy;
        }
        return [...prev, interaction];
      });
    }
  };

  const handleDeleteInteraction = (id: string) => {
    if (onDeleteInteraction) {
      onDeleteInteraction(id);
    } else {
      setLocalInteractionTypes((prev) => prev.filter((i) => i.id !== id));
    }
  };

  // Automatically activate the floor painting tool when Step 2 is selected
  useEffect(() => {
    if (activeStep === 2 && activeTool !== 'paint_floor') {
      setActiveTool('paint_floor');
    }
  }, [activeStep, activeTool, setActiveTool]);

  // Re-render trigger when new catalog items are created
  const [catalogVersion, setCatalogVersion] = useState<number>(0);

  // Local Floor Materials fallback if not provided by parent
  const [localFloorMaterials, setLocalFloorMaterials] = useState<FloorMaterialOption[]>(DEFAULT_FLOOR_MATERIALS);
  const activeFloorTypes = floorMaterials || localFloorMaterials;

  const updateFloorMaterials = (updater: (prev: FloorMaterialOption[]) => FloorMaterialOption[]) => {
    if (onFloorMaterialsChange) {
      onFloorMaterialsChange(updater);
    } else {
      setLocalFloorMaterials(updater);
    }
  };

  // --- Modal State: Create / Edit Floor Material with Checkerboard Engine ---
  const [showFloorModal, setShowFloorModal] = useState<boolean>(false);
  const [editingFloorMaterial, setEditingFloorMaterial] = useState<FloorMaterialOption | null>(null);

  const handleOpenCreateFloorModal = () => {
    setEditingFloorMaterial(null);
    setShowFloorModal(true);
  };

  const handleOpenEditFloorModal = (material: FloorMaterialOption, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setEditingFloorMaterial(material);
    setShowFloorModal(true);
  };

  const handleSaveFloorMaterial = (savedMaterial: FloorMaterialOption) => {
    updateFloorMaterials((prev) => {
      const existsIndex = prev.findIndex((m) => m.id === savedMaterial.id);
      if (existsIndex >= 0) {
        const copy = [...prev];
        copy[existsIndex] = savedMaterial;
        return copy;
      }
      return [...prev, savedMaterial];
    });

    setSelectedFloorType(savedMaterial.id);
    setActiveTool('paint_floor');
    setShowFloorModal(false);
    setEditingFloorMaterial(null);
  };

  // Inline editing state for Floor Material renaming on double-click
  const [editingFloorId, setEditingFloorId] = useState<string | null>(null);
  const [editingFloorName, setEditingFloorName] = useState<string>('');

  const handleStartRenameFloor = (ft: FloorMaterialOption, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setEditingFloorId(ft.id);
    setEditingFloorName(ft.name);
  };

  // --- Modal State: Configure Agent 16-Sprite Asset Set ---
  const [spriteModalAgent, setSpriteModalAgent] = useState<AIAgent | null>(null);

  const handleOpenAgentSpriteModal = (agent: AIAgent, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setSpriteModalAgent(agent);
  };

  const handleSaveAgentSpriteSet = (agentId: string, spriteSet: AgentAvatarSpriteSet, customImagePreview?: string) => {
    const updatedAgents = layout.agents.map((ag) =>
      ag.id === agentId
        ? {
            ...ag,
            spriteSet,
            customImage: customImagePreview || spriteSet.front_idle || ag.customImage,
          }
        : ag
    );
    onLayoutChange({ ...layout, agents: updatedAgents });
    if (selectedAgent && selectedAgent.id === agentId) {
      onAgentSelect?.({
        ...selectedAgent,
        spriteSet,
        customImage: customImagePreview || spriteSet.front_idle || selectedAgent.customImage,
      });
    }
    setSpriteModalAgent(null);
  };

  // Inline editing state for Furniture Item renaming on double-click
  const [editingFurnitureType, setEditingFurnitureType] = useState<string | null>(null);
  const [editingFurnitureName, setEditingFurnitureName] = useState<string>('');

  const handleStartRenameFurnitureType = (type: FurnitureType, currentName: string, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setEditingFurnitureType(type);
    setEditingFurnitureName(currentName);
  };

  // Category filter inside Step 3 (Furniture)
  const [activeCategory, setActiveCategory] = useState<FurnitureCategory>('workstation');
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState<boolean>(false);

  // Custom Categories list state
  const [customCategories, setCustomCategories] = useState<{ id: FurnitureCategory; name: string; icon: string }[]>([]);
  const [isCreatingNewCategory, setIsCreatingNewCategory] = useState<boolean>(false);
  const [newCategoryName, setNewCategoryName] = useState<string>('');
  const [newCategoryIcon, setNewCategoryIcon] = useState<string>('📦');

  // Base Categories list
  const baseCategories: { id: FurnitureCategory; name: string; icon: string }[] = [
    { id: 'workstation', name: 'Workstations', icon: '💻' },
    { id: 'structure', name: 'Walls / Doors', icon: '🧱' },
    { id: 'cafeteria', name: 'Breakroom / Kitchen', icon: '☕' },
    { id: 'restroom', name: 'Restrooms', icon: '🚽' },
    { id: 'meeting', name: 'Meeting Rooms', icon: '📊' },
    { id: 'equipment', name: 'Equipment / Tech', icon: '🖨️' },
  ];

  const categories = [...baseCategories, ...customCategories];

  const handleAddNewCategory = () => {
    const trimmed = newCategoryName.trim();
    if (!trimmed) {
      setIsCreatingNewCategory(false);
      return;
    }
    const catId = `cat_${trimmed.toLowerCase().replace(/\s+/g, '_')}_${Date.now()}` as FurnitureCategory;
    const newCat = {
      id: catId,
      name: trimmed,
      icon: newCategoryIcon.trim() || '📦',
    };
    setCustomCategories((prev) => [...prev, newCat]);
    setActiveCategory(catId);
    setNewCategoryName('');
    setNewCategoryIcon('📦');
    setIsCreatingNewCategory(false);
    setIsCategoryDropdownOpen(false);
  };

  // --- MCP Tools State ---
  const [mcpTools, setMcpTools] = useState<MCPToolDefinition[]>(DEFAULT_MCP_TOOLS);

  // --- Drawer State: Create New Furniture ---
  const [showCreateFurnitureDrawer, setShowCreateFurnitureDrawer] = useState<boolean>(false);

  const handleOpenCreateFurnitureDrawer = () => {
    setShowCreateFurnitureDrawer(true);
  };

  // Custom base grid size state & tile scale in meters
  const [customWidth, setCustomWidth] = useState<number>(layout.gridWidth);
  const [customHeight, setCustomHeight] = useState<number>(layout.gridHeight);
  const [customTileSizeStr, setCustomTileSizeStr] = useState<string>(String(layout.tileSizeMeters ?? 1.0));
  const [dimensionError, setDimensionError] = useState<string | null>(null);

  // Deletion confirmation states
  const [agentToDelete, setAgentToDelete] = useState<AIAgent | null>(null);

  useEffect(() => {
    setCustomWidth(layout.gridWidth);
    setCustomHeight(layout.gridHeight);
    setCustomTileSizeStr(String(layout.tileSizeMeters ?? 1.0));
  }, [layout.gridWidth, layout.gridHeight, layout.tileSizeMeters]);

  // Derived metric calculations from tile size
  const parsedTileSize = Math.max(0, parseFloat(customTileSizeStr.replace(',', '.')) || 0);
  const tileAreaM2 = parsedTileSize * parsedTileSize;
  const totalGridAreaM2 = customWidth * customHeight * tileAreaM2;

  // Apply Base Grid Dimension Change with Construction Validation
  const handleApplyGridDimensions = (w: number, h: number, rawTileSizeStr?: string) => {
    const validW = Math.max(4, Math.min(50, w));
    const validH = Math.max(4, Math.min(50, h));
    const strToParse = rawTileSizeStr !== undefined ? rawTileSizeStr : customTileSizeStr;
    const parsed = parseFloat(strToParse.replace(',', '.')) || 1.0;
    const validTileSize = Math.max(0.01, Math.min(20, parsed));

    // Calculate minimum required bounds from existing construction
    let minReqWidth = 1;
    let minReqHeight = 1;

    // Check custom floors
    if (layout.customFloors) {
      Object.keys(layout.customFloors).forEach((key) => {
        const [cStr, rStr] = key.split(',');
        const c = parseInt(cStr, 10);
        const r = parseInt(rStr, 10);
        if (c + 1 > minReqWidth) minReqWidth = c + 1;
        if (r + 1 > minReqHeight) minReqHeight = r + 1;
      });
    }

    // Check furniture items
    layout.items.forEach((item) => {
      const def = FURNITURE_CATALOG.find((f) => f.type === item.type);
      const itemW = def ? def.width : 1;
      const itemH = def ? def.height : 1;
      if (item.col + itemW > minReqWidth) minReqWidth = item.col + itemW;
      if (item.row + itemH > minReqHeight) minReqHeight = item.row + itemH;
    });

    // Check agents
    layout.agents.forEach((ag) => {
      const agC = Math.floor(ag.currentCol);
      const agR = Math.floor(ag.currentRow);
      if (agC + 1 > minReqWidth) minReqWidth = agC + 1;
      if (agR + 1 > minReqHeight) minReqHeight = agR + 1;
    });

    if (validW < minReqWidth || validH < minReqHeight) {
      setDimensionError('Cannot resize grid below existing items, agents, or custom floor tiles');
      setCustomWidth(layout.gridWidth);
      setCustomHeight(layout.gridHeight);
      return;
    }

    setDimensionError(null);
    setCustomWidth(validW);
    setCustomHeight(validH);
    setCustomTileSizeStr(String(validTileSize));

    onLayoutChange({
      ...layout,
      gridWidth: validW,
      gridHeight: validH,
      tileSizeMeters: validTileSize,
    });
  };

  // Deactivate selected agent (unplace from grid without deleting from system)
  const handleDeactivateAgent = (agentToDeactivate?: AIAgent | null) => {
    const target = agentToDeactivate || selectedAgent;
    if (!target) return;
    const updatedAgents = layout.agents.map((a) =>
      a.id === target.id
        ? {
            ...a,
            isPlaced: false,
            currentCol: -1,
            currentRow: -1,
            targetCol: null,
            targetRow: null,
            path: [],
            status: 'idle' as const,
          }
        : a
    );
    onLayoutChange({ ...layout, agents: updatedAgents });
    if (selectedAgent?.id === target.id && onAgentSelect) {
      onAgentSelect(null);
    }
  };

  // === RENDER ===
  return (
    <div id="office-sidebar" data-drawer-trigger="true" className="twin-office-editor">
      {/* === NAVEGACIÓN DE 4 PASOS === */}
      <div className="twin-office-editor-steps">
        <button
          type="button"
          onClick={() => {
            setActiveStep(1);
            setActiveTool('select');
            if (onSelectRoom) onSelectRoom(null);
            if (onSelectItem) onSelectItem(null);
            if (onAgentSelect) onAgentSelect(null);
            setSelectedFurnitureType(null);
            setSelectedFloorType(null);
            if (setRoomDraftBounds) setRoomDraftBounds(null);
          }}
          className={`twin-office-editor-step-btn ${activeStep === 1 ? 'twin-office-editor-step-btn--zones' : ''}`}
          title="Step 1: Zone & Room Boundaries"
        >
          <Layers className={`twin-icon-sm ${activeStep === 1 ? 'twin-icon-sm twin-color-theme-accent' : ''}`} />
          <span className="twin-office-editor-step-label">1. Zones</span>
        </button>

        <button
          type="button"
          onClick={() => {
            setActiveStep(2);
            setActiveTool('select');
            setSelectedFloorType(null);
            setSelectedFurnitureType(null);
            if (onSelectRoom) onSelectRoom(null);
            if (onSelectItem) onSelectItem(null);
            if (onAgentSelect) onAgentSelect(null);
            if (setRoomDraftBounds) setRoomDraftBounds(null);
          }}
          className={`twin-office-editor-step-btn ${activeStep === 2 ? 'twin-office-editor-step-btn--flooring' : ''}`}
          title="Step 2: Grid Dimensions & Floor Textures"
        >
          <Grid className={`twin-icon-sm ${activeStep === 2 ? 'twin-color-theme-accent' : ''}`} />
          <span className="twin-office-editor-step-label">2. Flooring</span>
        </button>

        <button
          type="button"
          onClick={() => {
            setActiveStep(3);
            setActiveTool('select');
            setSelectedFurnitureType(null);
            setSelectedFloorType(null);
            if (onSelectRoom) onSelectRoom(null);
            if (onSelectItem) onSelectItem(null);
            if (onAgentSelect) onAgentSelect(null);
            if (setRoomDraftBounds) setRoomDraftBounds(null);
          }}
          className={`twin-office-editor-step-btn ${activeStep === 3 ? 'twin-office-editor-step-btn--resources' : ''}`}
          title="Step 3: Furniture & Resource Catalog"
        >
          <Box className={`twin-icon-sm ${activeStep === 3 ? 'twin-color-amber' : ''}`} />
          <span className="twin-office-editor-step-label">3. Resources</span>
        </button>

        <button
          type="button"
          onClick={() => {
            setActiveStep(4);
            setActiveTool('select');
            if (onAgentSelect) onAgentSelect(null);
            if (onSelectRoom) onSelectRoom(null);
            if (onSelectItem) onSelectItem(null);
            setSelectedFurnitureType(null);
            setSelectedFloorType(null);
            if (setRoomDraftBounds) setRoomDraftBounds(null);
          }}
          className={`twin-office-editor-step-btn ${activeStep === 4 ? 'twin-office-editor-step-btn--agents' : ''}`}
          title="Step 4: Agent Roster & Placement"
        >
          <Bot className={`twin-icon-sm ${activeStep === 4 ? 'twin-color-emerald' : ''}`} />
          <span className="twin-office-editor-step-label">4. Agents</span>
        </button>
      </div>

      {/* === CONTENIDO DEL PASO ACTIVO === */}
      <div className="twin-office-editor-body">
        {/* PASO 1: DELIMITACIÓN DE ZONAS */}
        {activeStep === 1 && (
          <RoomManager
            layout={layout}
            onLayoutChange={onLayoutChange}
            selectedRoomId={selectedRoomId || null}
            onSelectRoom={onSelectRoom || (() => {})}
            activeTool={activeTool}
            setActiveTool={setActiveTool}
            onOverlapPrevented={onOverlapPrevented}
            roomDraftBounds={roomDraftBounds || null}
            setRoomDraftBounds={setRoomDraftBounds || (() => {})}
          />
        )}

        {/* PASO 2: DIMENSIONADO DE GRID Y PINTADO DE SUELO */}
        {activeStep === 2 && (
          <div className="twin-office-editor-step-panel">
            <div className="twin-office-editor-card">
              <div className="twin-office-editor-card-header">
                <span className="twin-office-editor-card-title">
                  <Grid className="twin-icon-sm twin-color-theme-accent" />
                  <span>2. Base Grid &amp; Real Scale</span>
                </span>
                <span className="twin-office-editor-card-badge">
                  {(customWidth * parsedTileSize).toFixed(2)}m × {(customHeight * parsedTileSize).toFixed(2)}m
                </span>
              </div>

              {/* Tile Size Field */}
              <div className="twin-office-editor-field-row">
                <div className="twin-office-editor-field-label-row">
                  <label className="twin-office-editor-field-label">Tile side length (scale):</label>
                  <span className="twin-office-editor-field-hint">
                    {tileAreaM2 > 0 ? `${tileAreaM2.toFixed(3).replace(/\.?0+$/, '')} m²/tile` : '0 m²'}
                  </span>
                </div>
                <div className="twin-office-editor-field-inline">
                  <span className="twin-office-editor-field-inline-label">1 tile =</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={customTileSizeStr}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (/^[0-9]*[.,]?[0-9]*$/.test(val)) {
                        setCustomTileSizeStr(val);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleApplyGridDimensions(customWidth, customHeight, customTileSizeStr);
                      }
                    }}
                    placeholder="1.0"
                    className="twin-office-editor-tile-input"
                  />
                  <span className="twin-office-editor-field-inline-label">meters (m)</span>
                </div>
              </div>

              {/* Grid Dimensions in Tiles */}
              <div className="twin-office-editor-dims-block">
                <label className="twin-office-editor-field-label">Grid size in tiles:</label>
                <div className="twin-office-editor-dims-row">
                  <div className="twin-office-editor-dim-group">
                    <span className="twin-office-editor-dim-label">Width:</span>
                    <div className="twin-office-editor-dim-input-wrap">
                      <input
                        type="number"
                        min={4}
                        max={50}
                        value={customWidth}
                        onChange={(e) => setCustomWidth(parseInt(e.target.value, 10) || 8)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleApplyGridDimensions(customWidth, customHeight, customTileSizeStr);
                          }
                        }}
                        className="twin-office-editor-dim-input"
                      />
                      <div className="twin-office-editor-dim-stepper">
                        <button
                          type="button"
                          onClick={() => setCustomWidth((prev) => Math.min(50, prev + 1))}
                          className="twin-office-editor-dim-stepper-btn"
                          title="Increase width"
                          tabIndex={-1}
                        >
                          <ChevronUp className="twin-icon-xxs" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setCustomWidth((prev) => Math.max(4, prev - 1))}
                          className="twin-office-editor-dim-stepper-btn"
                          title="Decrease width"
                          tabIndex={-1}
                        >
                          <ChevronDown className="twin-icon-xxs" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <span className="twin-office-editor-dims-x">x</span>
                  <div className="twin-office-editor-dim-group">
                    <span className="twin-office-editor-dim-label">Height:</span>
                    <div className="twin-office-editor-dim-input-wrap">
                      <input
                        type="number"
                        min={4}
                        max={50}
                        value={customHeight}
                        onChange={(e) => setCustomHeight(parseInt(e.target.value, 10) || 8)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleApplyGridDimensions(customWidth, customHeight, customTileSizeStr);
                          }
                        }}
                        className="twin-office-editor-dim-input"
                      />
                      <div className="twin-office-editor-dim-stepper">
                        <button
                          type="button"
                          onClick={() => setCustomHeight((prev) => Math.min(50, prev + 1))}
                          className="twin-office-editor-dim-stepper-btn"
                          title="Increase height"
                          tabIndex={-1}
                        >
                          <ChevronUp className="twin-icon-xxs" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setCustomHeight((prev) => Math.max(4, prev - 1))}
                          className="twin-office-editor-dim-stepper-btn"
                          title="Decrease height"
                          tabIndex={-1}
                        >
                          <ChevronDown className="twin-icon-xxs" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <span className="twin-office-editor-dims-unit">tiles</span>
                  <button
                    onClick={() => handleApplyGridDimensions(customWidth, customHeight, customTileSizeStr)}
                    className="twin-office-editor-apply-btn"
                    title="Apply new dimensions and metric scale"
                  >
                    Apply
                  </button>
                </div>
              </div>

              {/* Surface & Metrics Summary */}
              <div className="twin-office-editor-metrics-box">
                <div className="twin-office-editor-metrics-row">
                  <span className="twin-office-editor-metrics-label">Total Grid Surface Area:</span>
                  <span className="twin-office-editor-metrics-value">{totalGridAreaM2.toFixed(2)} m²</span>
                </div>
                <div className="twin-office-editor-metrics-subrow">
                  <span>
                    Single Tile: <strong className="twin-office-editor-metrics-strong">{tileAreaM2.toFixed(3).replace(/\.?0+$/, '')} m²</strong> ({parsedTileSize}m × {parsedTileSize}m)
                  </span>
                  <span className="twin-office-editor-metrics-mono">Total: {customWidth * customHeight} tiles</span>
                </div>
              </div>

              {dimensionError && (
                <div className="twin-office-editor-inline-error">
                  <AlertTriangle className="twin-icon-sm twin-office-editor-inline-error-icon" />
                  <span>{dimensionError}</span>
                </div>
              )}
            </div>

            {/* Floor Painting */}
            <div className="twin-office-editor-section">
              <div className="twin-office-editor-subheader">
                <span className="twin-office-editor-subheader-label">
                  <Paintbrush className="twin-icon-xs twin-color-theme-accent" />
                  <span>Paint Office Flooring</span>
                </span>
                <button
                  type="button"
                  onClick={handleOpenCreateFloorModal}
                  title="Create New Floor Material (Color or Image with Checkerboard Effect)"
                  className="twin-office-editor-plus-btn twin-room-manager-add-btn"
                >
                  <Plus className="twin-icon-sm" />
                </button>
              </div>

              <div className="twin-office-editor-floor-grid">
                {activeFloorTypes.map((ft) => {
                  const isSelected = selectedFloorType === ft.id && activeTool === 'paint_floor';
                  const isChecker = ft.hasCheckerboard !== false;

                  return (
                    <div
                      key={ft.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        if (isSelected) {
                          setSelectedFloorType(null);
                          setActiveTool('select');
                        } else {
                          setSelectedFloorType(ft.id);
                          setActiveTool('paint_floor');
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          if (isSelected) {
                            setSelectedFloorType(null);
                            setActiveTool('select');
                          } else {
                            setSelectedFloorType(ft.id);
                            setActiveTool('paint_floor');
                          }
                        }
                      }}
                      onDoubleClick={(e) => {
                        setSelectedFloorType(ft.id);
                        setActiveTool('paint_floor');
                        handleStartRenameFloor(ft, e);
                      }}
                      title="Click to select and paint • Double-click to rename"
                      className={`twin-office-editor-floor-card ${isSelected ? 'twin-office-editor-floor-card--selected' : ''}`}
                    >
                      <button
                        type="button"
                        onClick={(e) => handleOpenEditFloorModal(ft, e)}
                        title="Configure texture, colors, and checkerboard pattern"
                        className="twin-office-editor-floor-gear-btn"
                      >
                        <Settings className="twin-icon-xs" />
                      </button>

                      <div className="twin-office-editor-floor-preview-wrap">
                        <FloorTexturePreviewBox material={ft} isSelected={isSelected} square={true} />
                      </div>

                      <div className="twin-office-editor-floor-title-row">
                        <span className={`twin-office-editor-floor-name ${isSelected ? 'twin-office-editor-floor-name--selected' : ''}`}>{ft.name}</span>
                      </div>

                      <div className="twin-office-editor-floor-badges-row">
                        <span className="twin-office-editor-floor-mode-label">{ft.customImage ? '🖼️ Image' : '🎨 Color'}</span>
                        <span className={`twin-office-editor-floor-checker-badge ${isChecker ? 'twin-office-editor-floor-checker-badge--checker' : 'twin-office-editor-floor-checker-badge--solid'}`}>
                          {isChecker ? '♟️ Checker' : '⬛ Solid'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* PASO 3: COLOCACIÓN DE MOBILIARIO Y EQUIPO */}
        {activeStep === 3 && (
          <div className="twin-office-editor-step-panel twin-office-editor-step-panel--tight">
            <div className="twin-office-editor-category-block">
              <span className="twin-office-editor-category-title">
                <Box className="twin-icon-sm twin-color-amber" />
                <span>3. Resource Category</span>
              </span>
              <div className="twin-office-editor-category-trigger-row">
                {(() => {
                  const currentCat = categories.find((c) => c.id === activeCategory);
                  return (
                    <button type="button" onClick={() => setIsCategoryDropdownOpen((prev) => !prev)} className="twin-office-editor-category-trigger">
                      <div className="twin-office-editor-category-trigger-left">
                        <AppIcon icon={currentCat?.icon} category={currentCat?.id} className="twin-icon-sm twin-color-amber" />
                        <span className="twin-office-editor-category-trigger-name">{currentCat?.name || 'Select Category'}</span>
                        <span className="twin-office-editor-category-trigger-count">
                          {FURNITURE_CATALOG.filter((f) => f.category === activeCategory).length} items
                        </span>
                      </div>
                      <ChevronDown className={`twin-icon-sm twin-office-editor-category-chevron ${isCategoryDropdownOpen ? 'twin-office-editor-category-chevron--open' : ''}`} />
                    </button>
                  );
                })()}

                <button
                  type="button"
                  onClick={handleOpenCreateFurnitureDrawer}
                  className="twin-room-manager-add-btn"
                  title="Create New Resource"
                  aria-label="Create New Resource"
                >
                  <Plus className="twin-icon-sm" />
                </button>
              </div>

              {isCategoryDropdownOpen && (
                <>
                  <div
                    className="twin-office-editor-category-backdrop"
                    onClick={() => {
                      setIsCategoryDropdownOpen(false);
                      setIsCreatingNewCategory(false);
                    }}
                  />
                  <div className="twin-office-editor-category-dropdown">
                    {isCreatingNewCategory ? (
                      <div className="twin-office-editor-new-category-form">
                        <div className="twin-office-editor-new-category-header">
                          <span>New Category</span>
                          <button type="button" onClick={() => setIsCreatingNewCategory(false)} className="twin-office-editor-new-category-close">
                            <X className="twin-icon-xs" />
                          </button>
                        </div>
                        <div className="twin-office-editor-new-category-row">
                          <input
                            type="text"
                            placeholder="Emoji"
                            value={newCategoryIcon}
                            onChange={(e) => setNewCategoryIcon(e.target.value)}
                            className="twin-office-editor-icon-input"
                            maxLength={4}
                          />
                          <input
                            type="text"
                            placeholder="Category name..."
                            value={newCategoryName}
                            onChange={(e) => setNewCategoryName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddNewCategory();
                              }
                            }}
                            autoFocus
                            className="twin-office-editor-name-input"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={handleAddNewCategory}
                          disabled={!newCategoryName.trim()}
                          className="twin-office-editor-create-select-btn"
                        >
                          Create &amp; Select
                        </button>
                      </div>
                    ) : (
                      <button type="button" onClick={() => setIsCreatingNewCategory(true)} className="twin-office-editor-new-category-trigger">
                        <div className="twin-office-editor-new-category-trigger-left">
                          <PlusCircle className="twin-icon-sm twin-color-amber" />
                          <span>+ New Category</span>
                        </div>
                        <span className="twin-office-editor-new-category-badge">Create</span>
                      </button>
                    )}

                    {categories.map((cat) => {
                      const count = FURNITURE_CATALOG.filter((f) => f.category === cat.id).length;
                      const isSelected = activeCategory === cat.id;
                      return (
                        <button
                          key={cat.id}
                          type="button"
                          onClick={() => {
                            setActiveCategory(cat.id);
                            setIsCategoryDropdownOpen(false);
                            setIsCreatingNewCategory(false);
                          }}
                          className={`twin-office-editor-category-item ${isSelected ? 'twin-office-editor-category-item--selected' : ''}`}
                        >
                          <div className="twin-office-editor-category-item-left">
                            <AppIcon icon={cat.icon} category={cat.id} className="twin-icon-sm twin-color-amber" />
                            <span>{cat.name}</span>
                          </div>
                          <div className="twin-office-editor-category-item-right">
                            <span className="twin-office-editor-category-item-count">{count}</span>
                            {isSelected && <Check className="twin-icon-xs twin-color-amber" />}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {/* Furniture Grid Items */}
            <div className="twin-office-editor-furniture-grid">
              {FURNITURE_CATALOG.filter((f) => f.category === activeCategory).map((def) => {
                const isSelected = selectedFurnitureType === def.type && activeTool === 'place';
                return (
                  <button
                    key={def.type}
                    draggable
                    onDragStart={(e) => {
                      const dragData = JSON.stringify({
                        kind: 'furniture_catalog',
                        furnitureType: def.type,
                      });
                      e.dataTransfer.setData('application/json', dragData);
                      e.dataTransfer.setData('text/plain', def.type);
                      e.dataTransfer.effectAllowed = 'move';
                      createGraphicDragImage(e, def.icon, def.customImage);
                      setSelectedFurnitureType(def.type);
                      setActiveTool('place');
                    }}
                    onClick={() => {
                      if (isSelected) {
                        setSelectedFurnitureType(null);
                        setActiveTool('select');
                      } else {
                        setSelectedFurnitureType(def.type);
                        setActiveTool('place');
                      }
                    }}
                    onDoubleClick={(e) => {
                      setSelectedFurnitureType(def.type);
                      setActiveTool('place');
                      handleStartRenameFurnitureType(def.type, def.name, e);
                    }}
                    title="Click to select • Double-click to rename"
                    className={`twin-office-editor-furniture-card ${isSelected ? 'twin-office-editor-furniture-card--selected' : ''}`}
                  >
                    {def.customImage ? (
                      <img src={def.customImage} alt={def.name} className="twin-office-editor-furniture-img" />
                    ) : (
                      <AppIcon icon={def.icon} type={def.type} category={def.category} className="twin-icon-md twin-color-amber twin-office-editor-furniture-icon" />
                    )}
                    <span className="twin-office-editor-furniture-name">{def.name}</span>
                    <span className="twin-office-editor-furniture-dims">{def.width}x{def.height} tiles</span>
                    {def.mcpToolId && (
                      <span className="twin-office-editor-furniture-mcp-badge">
                        <Zap className="twin-icon-xxs" />
                        <span className="twin-office-editor-furniture-mcp-name">{mcpTools.find((t) => t.id === def.mcpToolId)?.name || 'MCP'}</span>
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* PASO 4: REFERENCIA Y UBICACIÓN DE AGENTES */}
        {activeStep === 4 && (
          <div className="twin-office-editor-step-panel twin-office-editor-step-panel--tight">
            <div className="twin-office-editor-agents-block">
              <div className="twin-office-editor-agents-header">
                <span className="twin-office-editor-section-header-label">
                  <Layers className="twin-icon-xs twin-color-theme-accent" />
                  <span className="twin-office-editor-subheader-label">Agent Roster</span>
                  
                </span>
                
                <span className="twin-office-editor-agents-count">
                    {layout.agents.filter(
                      (a) => a.isPlaced !== false && a.currentCol >= 0
                      ).length}/{layout.agents.length} active on layout
                </span>
              </div>

              {layout.agents.length === 0 ? (
                <div className="twin-office-editor-agents-empty">No agents registered in the system.</div>
              ) : (
                <div className="twin-office-editor-agents-grid">
                  {layout.agents.map((ag) => {
                    const roleConfig = AGENT_ROLES_CONFIG[ag.role];
                    const isSelected = selectedAgent?.id === ag.id;
                    const isPlaced = ag.isPlaced !== false && ag.currentCol >= 0 && ag.currentRow >= 0;

                    return (
                      <div
                        key={ag.id}
                        draggable
                        onDragStart={(e) => {
                          const dragData = JSON.stringify({
                            kind: 'agent',
                            agentId: ag.id,
                          });
                          e.dataTransfer.setData('application/json', dragData);
                          e.dataTransfer.setData('text/plain', ag.id);
                          e.dataTransfer.effectAllowed = 'move';
                          createGraphicDragImage(e, roleConfig?.icon || '🤖', ag.customImage);
                          onAgentSelect?.(ag);
                          setActiveTool('select');
                        }}
                        onClick={() => {
                          if (isSelected) {
                            onAgentSelect?.(null);
                          } else {
                            onAgentSelect?.(ag);
                            setActiveTool('select');
                          }
                        }}
                        title="Click to select • Drag to canvas to place"
                        className={`twin-office-editor-agent-card ${isSelected ? 'twin-office-editor-agent-card--selected' : ''}`}
                      >
                        <div className="twin-office-editor-agent-sprite-badge-wrap">
                          <button
                            type="button"
                            title="Configure 16-Sprite Movement Pack"
                            onClick={(e) => handleOpenAgentSpriteModal(ag, e)}
                            className="twin-office-editor-agent-sprite-badge"
                          >
                            <Sparkles className="twin-icon-xxs" />
                          </button>
                        </div>

                        <div className="twin-office-editor-agent-top-right">
                          {isPlaced ? (
                            <>
                              <span className="twin-office-editor-agent-coords-badge" title={`Active on grid (${Math.round(ag.currentCol)}, ${Math.round(ag.currentRow)})`}>
                                {Math.round(ag.currentCol)},{Math.round(ag.currentRow)}
                              </span>
                              <button
                                type="button"
                                title="Unplace and return to bench"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeactivateAgent(ag);
                                }}
                                className="twin-office-editor-agent-unplace-btn"
                              >
                                <UserMinus className="twin-icon-xs" />
                              </button>
                            </>
                          ) : (
                            <span className="twin-office-editor-agent-standby-badge">Standby</span>
                          )}
                        </div>

                        <div className="twin-office-editor-agent-avatar-wrap">
                          {ag.spriteSet?.front_idle || ag.customImage ? (
                            <img
                              src={ag.spriteSet?.front_idle || ag.customImage}
                              alt={ag.name}
                              className="twin-office-editor-agent-avatar-img"
                              style={{ imageRendering: 'pixelated' }}
                            />
                          ) : (
                            <div className="twin-office-editor-agent-avatar-fallback">
                              <AppIcon icon={roleConfig?.icon} role={ag.role} className="twin-icon-md twin-color-emerald" />
                            </div>
                          )}
                          <span className="twin-office-editor-agent-role-dot" style={{ backgroundColor: roleConfig?.color || '#10b981' }} />
                        </div>

                        <span className="twin-office-editor-agent-name">{ag.nickname?.trim() || ag.name}</span>
                        <span className="twin-office-editor-agent-role-title">{roleConfig?.title || 'Agent'}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* === MODAL DE ERROR DE DIMENSIONES === */}
      {dimensionError && (
        <div className="twin-office-editor-error-overlay">
          <div className="twin-office-editor-error-box">
            <button onClick={() => setDimensionError(null)} className="twin-office-editor-error-close-btn" title="Close">
              <X className="twin-icon-xs" />
            </button>

            <div className="twin-office-editor-error-content">
              <div className="twin-office-editor-error-icon-wrap">
                <AlertTriangle className="twin-icon-md" />
              </div>

              <div className="twin-office-editor-error-text">
                <h3 className="twin-office-editor-error-title">Invalid Dimensions</h3>
                <p className="twin-office-editor-error-desc">{dimensionError}</p>
              </div>
            </div>

            <div className="twin-office-editor-error-footer">
              <button onClick={() => setDimensionError(null)} className="twin-office-editor-error-confirm-btn">
                Understood
              </button>
            </div>
          </div>
        </div>
      )}

      {/* === DRAWER: MATERIAL DE SUELO AVANZADO === */}
      <FloorMaterialDrawer
        isOpen={showFloorModal}
        onClose={() => {
          setShowFloorModal(false);
          setEditingFloorMaterial(null);
        }}
        onSave={handleSaveFloorMaterial}
        onDelete={onOpenDeleteFloorModal}
        initialMaterial={editingFloorMaterial}
      />

      {/* === DRAWER: CREAR NUEVO MOBILIARIO/RECURSO === */}
      <CreateFurnitureDrawer
        isOpen={showCreateFurnitureDrawer}
        onClose={() => setShowCreateFurnitureDrawer(false)}
        onSave={(newDef) => {
          addCustomFurnitureDefinition(newDef);
          setCatalogVersion((v) => v + 1);
          setActiveCategory(newDef.category);
          setSelectedFurnitureType(newDef.type);
          setActiveTool('place');
        }}
        categories={categories}
        activeCategory={activeCategory}
        activeInteractionTypes={activeInteractionTypes}
        mcpTools={mcpTools}
        onAddNewMcpTool={(tool) => setMcpTools((prev) => [...prev, tool])}
        onOpenInteractionGuide={() => {
          setInteractionManagerTab('guide');
          setShowInteractionManagerModal(true);
        }}
        onOpenNewInteractionModal={() => {
          setInteractionManagerTab('create');
          setShowInteractionManagerModal(true);
        }}
      />

      {/* === MODAL: GESTOR DE INTERACCIONES Y AYUDA === */}
      <InteractionManagerModal
        isOpen={showInteractionManagerModal}
        onClose={() => setShowInteractionManagerModal(false)}
        interactions={activeInteractionTypes}
        onSaveInteraction={handleSaveInteraction}
        onDeleteInteraction={handleDeleteInteraction}
        layout={layout}
        initialTab={interactionManagerTab}
        onSelectForFurniture={(_id) => {
          setShowCreateFurnitureDrawer(true);
        }}
      />

      {/* === MODAL DE CONFIRMACIÓN: DESACTIVAR AGENTE === */}
      <ConfirmDeleteModal
        isOpen={Boolean(agentToDelete)}
        onClose={() => setAgentToDelete(null)}
        onConfirm={() => {
          if (agentToDelete) {
            handleDeactivateAgent(agentToDelete);
            setAgentToDelete(null);
          }
        }}
        title="Unplace Agent from Grid?"
        itemName={agentToDelete ? agentToDelete.nickname?.trim() || agentToDelete.name : ''}
        itemIcon={agentToDelete ? AGENT_ROLES_CONFIG[agentToDelete.role]?.icon || 'bot' : 'bot'}
        itemType="agent"
        description={
          agentToDelete
            ? `Do you want to unplace "${agentToDelete.nickname?.trim() || agentToDelete.name}" (${AGENT_ROLES_CONFIG[agentToDelete.role]?.title || 'Agent'}) from the grid? The agent will return to the standby roster. Agents are permanent system entities and cannot be deleted.`
            : undefined
        }
        confirmText="Unplace Agent"
      />

      {/* === MODAL: CONFIGURACIÓN DE 16 SPRITES DE MOVIMIENTO === */}
      {spriteModalAgent && (
        <AgentAvatarSpriteModal
          agent={spriteModalAgent}
          isOpen={Boolean(spriteModalAgent)}
          onClose={() => setSpriteModalAgent(null)}
          onSaveSpriteSet={handleSaveAgentSpriteSet}
        />
      )}
    </div>
  );
};